import React, { useState, useEffect } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';
import { getMergedBookings } from '../services/mergedBookingsApi';
import { MergedBookingDay } from '../types/MergedBookings';
import { updateBookingConfirmation, deleteBooking, createBooking, updateBookingAmountPaid, updateBookingSetLength, reorderLineup } from '../services/bookingsApi';
import { getComics } from '../services/comicsApi';
import { Comic } from '../types/Comic';
import { getStaff } from '../services/staffApi';
import { Staff } from '../types/Staff';
import { createStaffBooking, deleteStaffBooking, updateStaffBookingConfirmation } from '../services/staffBookingsApi';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import './BookingsList.css';

// Sortable row component for drag-and-drop
interface SortableRowProps {
  bookingKey: string;
  booking: any;
  isUpdating: boolean;
  editingAmountPaid: string | null;
  amountPaidValues: { [key: string]: string };
  editingSetLength: string | null;
  setLengthValues: { [key: string]: string };
  setEditingAmountPaid: (key: string | null) => void;
  setAmountPaidValues: (values: { [key: string]: string }) => void;
  setEditingSetLength: (key: string | null) => void;
  setSetLengthValues: (values: { [key: string]: string }) => void;
  handleToggleConfirmation: (showDateTime: string, comicEmail: string, currentStatus: boolean, comicName: string) => void;
  handleUpdateAmountPaid: (showDateTime: string, comicEmail: string, comicName: string) => void;
  handleUpdateSetLength: (showDateTime: string, comicEmail: string, comicName: string) => void;
  handleDelete: (showDateTime: string, comicEmail: string, comicName: string) => void;
  index: number;
}

const SortableBookingRow: React.FC<SortableRowProps> = ({
  bookingKey,
  booking,
  isUpdating,
  editingAmountPaid,
  amountPaidValues,
  editingSetLength,
  setLengthValues,
  setEditingAmountPaid,
  setAmountPaidValues,
  setEditingSetLength,
  setSetLengthValues,
  handleToggleConfirmation,
  handleUpdateAmountPaid,
  handleUpdateSetLength,
  handleDelete,
  index,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: bookingKey });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <tr
      ref={setNodeRef}
      style={style}
      className={booking.confirmed ? 'confirmed-row' : 'unconfirmed-row'}
    >
      <td className="drag-handle" {...attributes} {...listeners}>
        <span className="drag-icon">☰</span>
        <span className="lineup-number">{index + 1}</span>
      </td>
      <td className="comic-name">{booking.comic_name}</td>
      <td>
        {booking.is_host ? (
          <span className="role-badge role-host">Host</span>
        ) : (
          <span className="role-badge role-comic">Comic</span>
        )}
      </td>
      <td>
        {editingSetLength === bookingKey ? (
          <div className="set-length-edit">
            <input
              type="number"
              value={setLengthValues[bookingKey] || ''}
              onChange={(e) => setSetLengthValues({ ...setLengthValues, [bookingKey]: e.target.value })}
              placeholder="0"
              min="1"
              className="set-length-input"
            />
            <button
              onClick={() => handleUpdateSetLength(booking.show_date_time, booking.comic_email, booking.comic_name)}
              disabled={isUpdating}
              className="save-amount-button"
            >
              ✓
            </button>
            <button
              onClick={() => setEditingSetLength(null)}
              disabled={isUpdating}
              className="cancel-amount-button"
            >
              ✕
            </button>
          </div>
        ) : (
          <div
            className="set-length-display"
            onClick={() => {
              setEditingSetLength(bookingKey);
              setSetLengthValues({ ...setLengthValues, [bookingKey]: booking.set_length?.toString() || '' });
            }}
          >
            {booking.set_length ? (
              <span className="set-length">{booking.set_length} min</span>
            ) : (
              <span className="set-length-empty">Click to set</span>
            )}
          </div>
        )}
      </td>
      <td>
        {editingAmountPaid === bookingKey ? (
          <div className="amount-paid-edit">
            <input
              type="number"
              value={amountPaidValues[bookingKey] || ''}
              onChange={(e) => setAmountPaidValues({ ...amountPaidValues, [bookingKey]: e.target.value })}
              placeholder="0"
              min="0"
              className="amount-paid-input"
            />
            <button
              onClick={() => handleUpdateAmountPaid(booking.show_date_time, booking.comic_email, booking.comic_name)}
              disabled={isUpdating}
              className="save-amount-button"
            >
              ✓
            </button>
            <button
              onClick={() => setEditingAmountPaid(null)}
              disabled={isUpdating}
              className="cancel-amount-button"
            >
              ✕
            </button>
          </div>
        ) : (
          <div
            className="amount-paid-display"
            onClick={() => {
              setEditingAmountPaid(bookingKey);
              setAmountPaidValues({ ...amountPaidValues, [bookingKey]: booking.amount_paid?.toString() || '' });
            }}
          >
            {booking.amount_paid !== undefined && booking.amount_paid !== null ? (
              <span className="amount-paid">${booking.amount_paid}</span>
            ) : (
              <span className="amount-unpaid">Click to set</span>
            )}
          </div>
        )}
      </td>
      <td>
        {booking.confirmed ? (
          <span className="status-badge status-confirmed">✓ Confirmed</span>
        ) : (
          <span className="status-badge status-unconfirmed">⏳ Pending</span>
        )}
      </td>
      <td>
        {booking.email_sent ? (
          <span className="email-badge email-sent">✓ Sent</span>
        ) : (
          <span className="email-badge email-not-sent">✕ Not sent</span>
        )}
      </td>
      <td className="actions-cell">
        {!booking.confirmed && (
          <button
            onClick={() => handleToggleConfirmation(booking.show_date_time, booking.comic_email, booking.confirmed, booking.comic_name)}
            disabled={isUpdating}
            className="toggle-button btn-confirm"
          >
            ✓ Confirm
          </button>
        )}
        <button
          onClick={() => handleDelete(booking.show_date_time, booking.comic_email, booking.comic_name)}
          disabled={isUpdating}
          className="delete-button"
        >
          Delete
        </button>
      </td>
    </tr>
  );
};

const BookingsList: React.FC = () => {
  const [bookingDays, setBookingDays] = useState<MergedBookingDay[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [updatingBooking, setUpdatingBooking] = useState<string | null>(null);
  const [editingAmountPaid, setEditingAmountPaid] = useState<string | null>(null);
  const [amountPaidValues, setAmountPaidValues] = useState<{ [key: string]: string }>({});
  const [editingSetLength, setEditingSetLength] = useState<string | null>(null);
  const [setLengthValues, setSetLengthValues] = useState<{ [key: string]: string }>({});
  const [reorderingShow, setReorderingShow] = useState<string | null>(null);

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Filters - default to previous weekend (starting Friday) through next weekend
  const getDefaultDateRange = () => {
    const today = new Date();
    const dayOfWeek = today.getDay(); // 0 = Sunday, 6 = Saturday, 5 = Friday

    // Calculate previous Friday
    const previousFriday = new Date(today);
    if (dayOfWeek === 5) {
      // If today is Friday, go back 7 days to last Friday
      previousFriday.setDate(today.getDate() - 7);
    } else if (dayOfWeek === 6) {
      // If today is Saturday, go back 8 days to last Friday
      previousFriday.setDate(today.getDate() - 8);
    } else {
      // Otherwise, go back to the most recent Friday
      // Sunday (0) -> -2, Monday (1) -> -3, Tuesday (2) -> -4, Wednesday (3) -> -5, Thursday (4) -> -6
      previousFriday.setDate(today.getDate() - (dayOfWeek + 2));
    }

    // Calculate next Sunday
    const nextSunday = new Date(today);
    if (dayOfWeek === 0) {
      // If today is Sunday, go forward 7 days to next Sunday
      nextSunday.setDate(today.getDate() + 7);
    } else {
      // Otherwise, go forward to the upcoming Sunday
      nextSunday.setDate(today.getDate() + (7 - dayOfWeek));
    }

    return {
      start: previousFriday.toISOString().split('T')[0],
      end: nextSunday.toISOString().split('T')[0]
    };
  };

  const defaultRange = getDefaultDateRange();
  const [startDate, setStartDate] = useState(defaultRange.start);
  const [endDate, setEndDate] = useState(defaultRange.end);

  // Add Booking Form
  const [showAddForm, setShowAddForm] = useState(false);
  const [bookingCategory, setBookingCategory] = useState<'comic' | 'staff'>('comic');
  const [newBooking, setNewBooking] = useState({
    comic_email: '',
    comic_name: '',
    staff_email: '',
    staff_name: '',
    show_date: '',
    show_time: '19:00',
    is_host: false,
    guest_spot_name: '',
    set_length: '',
    send_email: false,
    staff_roles: [] as string[],
    staff_start_hour: '',
    staff_end_hour: ''
  });

  // Comics Autocomplete
  const [comics, setComics] = useState<Comic[]>([]);
  const [filteredComics, setFilteredComics] = useState<Comic[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);

  // Staff Autocomplete
  const [staff, setStaff] = useState<Staff[]>([]);
  const [filteredStaff, setFilteredStaff] = useState<Staff[]>([]);
  const [staffSearchQuery, setStaffSearchQuery] = useState('');
  const [showStaffSuggestions, setShowStaffSuggestions] = useState(false);

  useEffect(() => {
    loadBookings();
  }, []);

  useEffect(() => {
    if (showAddForm) {
      loadComics();
      loadStaff();
    }
  }, [showAddForm]);

  const loadBookings = async () => {
    try {
      setLoading(true);
      setError(null);
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString() || '';

      const filters: any = {};
      if (startDate) filters.start_date = startDate;
      if (endDate) filters.end_date = endDate;

      const data = await getMergedBookings(token, filters);
      setBookingDays(data.bookings || []);
    } catch (err: any) {
      setError(err.message || 'Failed to load bookings');
      console.error('Error loading bookings:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadComics = async () => {
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString() || '';
      const data = await getComics(token);
      setComics(data || []);
    } catch (err: any) {
      console.error('Error loading comics:', err);
    }
  };

  const loadStaff = async () => {
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString() || '';
      const data = await getStaff(token);
      setStaff(data || []);
    } catch (err: any) {
      console.error('Error loading staff:', err);
    }
  };

  const handleApplyFilters = () => {
    loadBookings();
  };

  const handleClearFilters = () => {
    setStartDate('');
    setEndDate('');
    setTimeout(() => loadBookings(), 0);
  };

  const handleToggleConfirmation = async (showDateTime: string, comicEmail: string, currentStatus: boolean, comicName: string) => {
    const bookingKey = `${showDateTime}-${comicEmail}`;
    setUpdatingBooking(bookingKey);
    setError(null);
    setSuccessMessage(null);

    try {
      await updateBookingConfirmation(showDateTime, comicEmail, !currentStatus);
      setSuccessMessage(`${comicName} marked as ${!currentStatus ? 'confirmed' : 'unconfirmed'}`);
      setTimeout(() => setSuccessMessage(null), 3000);

      // Update local state instead of reloading
      setBookingDays(prevDays =>
        prevDays.map(day => ({
          ...day,
          shows: day.shows?.map(show =>
            show.show_date_time === showDateTime
              ? {
                  ...show,
                  comic_bookings: show.comic_bookings?.map(booking =>
                    booking.comic_email === comicEmail
                      ? { ...booking, confirmed: !currentStatus }
                      : booking
                  ),
                }
              : show
          ),
        }))
      );
    } catch (err: any) {
      console.error('Error updating booking:', err);
      setError(err.message || 'Failed to update booking');
    } finally {
      setUpdatingBooking(null);
    }
  };

  const handleDeleteBooking = async (showDateTime: string, comicEmail: string, comicName: string) => {
    if (!window.confirm(`Are you sure you want to delete the booking for ${comicName}?`)) {
      return;
    }

    const bookingKey = `${showDateTime}-${comicEmail}`;
    setUpdatingBooking(bookingKey);
    setError(null);
    setSuccessMessage(null);

    try {
      await deleteBooking(showDateTime, comicEmail);
      setSuccessMessage(`Deleted booking for ${comicName}`);
      setTimeout(() => setSuccessMessage(null), 3000);
      await loadBookings();
    } catch (err: any) {
      console.error('Error deleting booking:', err);
      setError(err.message || 'Failed to delete booking');
    } finally {
      setUpdatingBooking(null);
    }
  };

  const handleUpdateAmountPaid = async (showDateTime: string, comicEmail: string, comicName: string) => {
    const bookingKey = `${showDateTime}-${comicEmail}`;
    const newAmount = amountPaidValues[bookingKey];

    if (newAmount === undefined || newAmount === '') {
      setError('Please enter an amount');
      return;
    }

    setUpdatingBooking(bookingKey);
    setError(null);
    setSuccessMessage(null);

    try {
      await updateBookingAmountPaid(showDateTime, comicEmail, parseInt(newAmount));
      setSuccessMessage(`Updated payment for ${comicName} to $${newAmount}`);
      setTimeout(() => setSuccessMessage(null), 3000);
      setEditingAmountPaid(null);

      // Update local state instead of reloading
      setBookingDays(prevDays =>
        prevDays.map(day => ({
          ...day,
          shows: day.shows.map(show => ({
            ...show,
            comic_bookings: show.comic_bookings.map(booking =>
              booking.show_date_time === showDateTime && booking.comic_email === comicEmail
                ? { ...booking, amount_paid: parseInt(newAmount) }
                : booking
            )
          }))
        }))
      );
    } catch (err: any) {
      console.error('Error updating amount paid:', err);
      setError(err.message || 'Failed to update amount paid');
    } finally {
      setUpdatingBooking(null);
    }
  };

  const handleUpdateSetLength = async (showDateTime: string, comicEmail: string, comicName: string) => {
    const bookingKey = `${showDateTime}-${comicEmail}`;
    const newSetLength = setLengthValues[bookingKey];

    if (newSetLength === undefined || newSetLength === '') {
      setError('Please enter a set length');
      return;
    }

    setUpdatingBooking(bookingKey);
    setError(null);
    setSuccessMessage(null);

    try {
      await updateBookingSetLength(showDateTime, comicEmail, parseInt(newSetLength));
      setSuccessMessage(`Updated set length for ${comicName} to ${newSetLength} min`);
      setTimeout(() => setSuccessMessage(null), 3000);
      setEditingSetLength(null);

      // Update local state instead of reloading
      setBookingDays(prevDays =>
        prevDays.map(day => ({
          ...day,
          shows: day.shows.map(show => ({
            ...show,
            comic_bookings: show.comic_bookings.map(booking =>
              booking.show_date_time === showDateTime && booking.comic_email === comicEmail
                ? { ...booking, set_length: parseInt(newSetLength) }
                : booking
            )
          }))
        }))
      );
    } catch (err: any) {
      console.error('Error updating set length:', err);
      setError(err.message || 'Failed to update set length');
    } finally {
      setUpdatingBooking(null);
    }
  };

  const handleDragEnd = async (event: DragEndEvent, showDateTime: string, bookings: any[]) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = bookings.findIndex(b => `${b.show_date_time}-${b.comic_email}` === active.id);
    const newIndex = bookings.findIndex(b => `${b.show_date_time}-${b.comic_email}` === over.id);

    if (oldIndex !== -1 && newIndex !== -1) {
      const movingBooking = bookings[oldIndex];

      // Rule: Host must always be first
      // Prevent moving a host away from position 0
      if (movingBooking.is_host && newIndex !== 0) {
        setError('The host must always go first in the lineup');
        setTimeout(() => setError(null), 3000);
        return;
      }

      // Prevent moving a non-host into position 0 if there's already a host
      const hasHost = bookings.some(b => b.is_host);
      if (!movingBooking.is_host && newIndex === 0 && hasHost) {
        setError('The host must always go first in the lineup');
        setTimeout(() => setError(null), 3000);
        return;
      }

      // Update bookings locally
      const reorderedBookings = arrayMove(bookings, oldIndex, newIndex);

      // Update the state with the new order
      setBookingDays(prevDays =>
        prevDays.map(day => ({
          ...day,
          shows: day.shows?.map(show =>
            show.show_date_time === showDateTime
              ? { ...show, comic_bookings: reorderedBookings }
              : show
          ),
        }))
      );

      // Auto-save the lineup order
      setReorderingShow(showDateTime);

      try {
        // Create lineup array with order
        const lineup = reorderedBookings.map((booking, index) => ({
          comic_email: booking.comic_email,
          order: index + 1,
        }));

        console.log('Saving lineup order:', { showDateTime, lineup });
        const result = await reorderLineup(showDateTime, lineup);
        console.log('Lineup order save result:', result);
        setSuccessMessage('Lineup order saved');
        setTimeout(() => {
          setSuccessMessage(null);
          setReorderingShow(null);
        }, 2000);
      } catch (err: any) {
        console.error('Error saving lineup order:', err);
        console.error('Error details:', err.response?.data);
        setError(err.response?.data?.error || err.message || 'Failed to save lineup order');
        setReorderingShow(null);
        // Reload to revert changes
        await loadBookings();
      }
    }
  };

  const handleComicSearch = (query: string) => {
    setSearchQuery(query);
    if (query.length > 0) {
      const filtered = comics.filter(
        (comic) =>
          comic.name.toLowerCase().includes(query.toLowerCase()) ||
          comic.email.toLowerCase().includes(query.toLowerCase())
      );
      setFilteredComics(filtered);
      setShowSuggestions(true);
    } else {
      setFilteredComics([]);
      setShowSuggestions(false);
    }
  };

  const selectComic = (comic: Comic) => {
    setNewBooking({
      ...newBooking,
      comic_name: comic.name,
      comic_email: comic.email
    });
    setSearchQuery(comic.name);
    setShowSuggestions(false);
  };

  const handleStaffSearch = (query: string) => {
    setStaffSearchQuery(query);
    if (query.length > 0) {
      const filtered = staff.filter(
        (s) =>
          s.name.toLowerCase().includes(query.toLowerCase()) ||
          s.email.toLowerCase().includes(query.toLowerCase())
      );
      setFilteredStaff(filtered);
      setShowStaffSuggestions(true);
    } else {
      setFilteredStaff([]);
      setShowStaffSuggestions(false);
    }
  };

  const selectStaff = (staffMember: Staff) => {
    setNewBooking({
      ...newBooking,
      staff_name: staffMember.name,
      staff_email: staffMember.email
    });
    setStaffSearchQuery(staffMember.name);
    setShowStaffSuggestions(false);
  };

  const handleToggleStaffConfirmation = async (date: string, staffEmail: string, currentStatus: boolean, staffName: string) => {
    setError(null);
    setSuccessMessage(null);

    try {
      const newStatus = !currentStatus;
      await updateStaffBookingConfirmation(date, staffEmail, newStatus);
      setSuccessMessage(`${newStatus ? 'Confirmed' : 'Unconfirmed'} staff booking for ${staffName}`);
      setTimeout(() => setSuccessMessage(null), 3000);

      // Update local state without full refresh
      setBookingDays(prevDays =>
        prevDays.map(day =>
          day.date === date
            ? {
                ...day,
                staff_bookings: day.staff_bookings?.map(booking =>
                  booking.staff_email === staffEmail
                    ? { ...booking, confirmed: newStatus }
                    : booking
                )
              }
            : day
        )
      );
    } catch (err: any) {
      console.error('Error updating staff booking:', err);
      setError(err.message || 'Failed to update staff booking');
    }
  };

  const handleDeleteStaffBooking = async (date: string, staffEmail: string, staffName: string) => {
    if (!window.confirm(`Are you sure you want to delete the staff booking for ${staffName}?`)) {
      return;
    }

    setError(null);
    setSuccessMessage(null);

    try {
      await deleteStaffBooking(date, staffEmail);
      setSuccessMessage(`Deleted staff booking for ${staffName}`);
      setTimeout(() => setSuccessMessage(null), 3000);
      await loadBookings();
    } catch (err: any) {
      console.error('Error deleting staff booking:', err);
      setError(err.message || 'Failed to delete staff booking');
    }
  };

  const handleAddBooking = async () => {
    setError(null);
    setSuccessMessage(null);

    // Validate date/time
    if (!newBooking.show_date || !newBooking.show_time) {
      setError('Please select show date and time');
      return;
    }

    try {
      if (bookingCategory === 'comic') {
        // COMIC BOOKING - check if either passed comic OR guest spot is filled
        const hasPassedComic = newBooking.comic_email && newBooking.comic_name;
        const hasGuestSpot = newBooking.guest_spot_name && newBooking.set_length;

        if (!hasPassedComic && !hasGuestSpot) {
          setError('Please either select a passed comic OR fill in guest spot details');
          return;
        }

        if (hasPassedComic && hasGuestSpot) {
          setError('Please use either passed comic OR guest spot, not both');
          return;
        }

        // Combine date and time into ISO format
        const show_date_time = `${newBooking.show_date}T${newBooking.show_time}:00`;

        // Determine if this is a guest spot
        const isGuestSpot = hasGuestSpot;
        const finalComicName = isGuestSpot ? newBooking.guest_spot_name : newBooking.comic_name;
        const finalComicEmail = isGuestSpot ? 'guest@highlinecomedy.com' : newBooking.comic_email;

        const bookingRequest = {
          comic_email: finalComicEmail,
          comic_name: finalComicName,
          show_date_time: show_date_time,
          show_display: '', // Backend will auto-generate
          is_host: newBooking.is_host,
          send_email: newBooking.send_email && !isGuestSpot, // Only send email if checkbox is checked AND not a guest spot
          ...(isGuestSpot && newBooking.set_length && { set_length: parseInt(newBooking.set_length) })
        };

        await createBooking(bookingRequest);
        setSuccessMessage(`Booking created for ${finalComicName}`);
        setTimeout(() => setSuccessMessage(null), 3000);
      } else {
        // STAFF BOOKING
        if (!newBooking.staff_email || !newBooking.staff_name) {
          setError('Please select a staff member from autocomplete');
          return;
        }

        // Date format for staff bookings (YYYY-MM-DD)
        const date = newBooking.show_date;

        // Generate date_display (e.g., "Wed, Jan 15, 2025")
        const dateObj = new Date(date);
        const options: Intl.DateTimeFormatOptions = {
          weekday: 'short',
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        };
        const date_display = dateObj.toLocaleDateString('en-US', options);

        const staffBookingRequest = {
          staff_email: newBooking.staff_email,
          staff_name: newBooking.staff_name,
          date: date,
          date_display: date_display,
          roles: newBooking.staff_roles,
          start_hour: newBooking.staff_start_hour || undefined,
          end_hour: newBooking.staff_end_hour || undefined,
          send_email: newBooking.send_email // STAFF email checkbox only applies to staff
        };

        await createStaffBooking(staffBookingRequest);
        setSuccessMessage(`Staff booking created for ${newBooking.staff_name}`);
        setTimeout(() => setSuccessMessage(null), 3000);
      }

      // Reset form
      setNewBooking({
        comic_email: '',
        comic_name: '',
        staff_email: '',
        staff_name: '',
        show_date: '',
        show_time: '19:00',
        is_host: false,
        guest_spot_name: '',
        set_length: '',
        send_email: false,
        staff_roles: [],
        staff_start_hour: '',
        staff_end_hour: ''
      });
      setSearchQuery('');
      setStaffSearchQuery('');
      setShowAddForm(false);

      await loadBookings();
    } catch (err: any) {
      console.error('Error creating booking:', err);
      setError(err.message || 'Failed to create booking');
    }
  };

  return (
    <div className="bookings-list">
      <div className="bookings-header">
        <h2>Bookings Schedule</h2>
        <div className="header-actions">
          <button
            onClick={() => setShowAddForm(!showAddForm)}
            className="add-booking-button"
          >
            {showAddForm ? 'Cancel' : '+ Add Booking'}
          </button>
          <button onClick={loadBookings} className="refresh-button" disabled={loading}>
            {loading ? 'Loading...' : '🔄 Refresh'}
          </button>
        </div>
      </div>

      {error && (
        <div className="error-message">
          {error}
          <button onClick={() => setError(null)} className="close-error">×</button>
        </div>
      )}

      {successMessage && (
        <div className="success-message">
          {successMessage}
          <button onClick={() => setSuccessMessage(null)} className="close-success">×</button>
        </div>
      )}

      {showAddForm && (
        <div className="add-booking-form">
          <h3>Add New Booking</h3>

          {/* TOP LEVEL: Radio buttons for Comic vs Staff */}
          <div className="booking-category-radio">
            <label className="radio-label">
              <input
                type="radio"
                name="bookingCategory"
                value="comic"
                checked={bookingCategory === 'comic'}
                onChange={(e) => setBookingCategory(e.target.value as 'comic' | 'staff')}
              />
              <span>Comic</span>
            </label>
            <label className="radio-label">
              <input
                type="radio"
                name="bookingCategory"
                value="staff"
                checked={bookingCategory === 'staff'}
                onChange={(e) => setBookingCategory(e.target.value as 'comic' | 'staff')}
              />
              <span>Staff</span>
            </label>
          </div>

          {bookingCategory === 'comic' ? (
            <>
              {/* PASSED COMIC SECTION */}
              <div className="booking-type-section">
                <h4>Passed Comic (from Database)</h4>
                <div className="form-grid">
                  <div className="form-group autocomplete-container">
                    <label htmlFor="comic-search">Comic Name</label>
                    <input
                      type="text"
                      id="comic-search"
                      value={searchQuery}
                      onChange={(e) => handleComicSearch(e.target.value)}
                      onFocus={() => searchQuery && setShowSuggestions(true)}
                      onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                      placeholder="Start typing comic name..."
                      autoComplete="off"
                    />
                    {showSuggestions && filteredComics.length > 0 && (
                      <ul className="autocomplete-suggestions">
                        {filteredComics.map((comic) => (
                          <li
                            key={comic.email}
                            onClick={() => selectComic(comic)}
                            className="suggestion-item"
                          >
                            <div className="suggestion-name">{comic.name}</div>
                            <div className="suggestion-email">{comic.email}</div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor="comic-email">Comic Email</label>
                    <input
                      type="email"
                      id="comic-email"
                      value={newBooking.comic_email}
                      onChange={(e) => setNewBooking({ ...newBooking, comic_email: e.target.value })}
                      placeholder="Auto-filled from selection"
                      readOnly
                    />
                  </div>

                  <div className="form-group checkbox-group">
                    <label>
                      <input
                        type="checkbox"
                        checked={newBooking.send_email}
                        onChange={(e) => setNewBooking({ ...newBooking, send_email: e.target.checked })}
                      />
                      Send confirmation email to comic
                    </label>
                  </div>
                </div>
              </div>

              {/* OR DIVIDER */}
              <div className="form-divider">
                <span>OR</span>
              </div>

              {/* GUEST SPOT SECTION */}
              <div className="booking-type-section">
                <h4>Guest Spot (Not in Database)</h4>
                <div className="form-grid">
                  <div className="form-group">
                    <label htmlFor="guest-spot-name">Guest Name</label>
                    <input
                      type="text"
                      id="guest-spot-name"
                      value={newBooking.guest_spot_name}
                      onChange={(e) => setNewBooking({ ...newBooking, guest_spot_name: e.target.value })}
                      placeholder="Guest comic name"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="set-length">Set Time (minutes)</label>
                    <input
                      type="number"
                      id="set-length"
                      value={newBooking.set_length}
                      onChange={(e) => setNewBooking({ ...newBooking, set_length: e.target.value })}
                      placeholder="e.g., 5, 7, 10"
                      min="1"
                    />
                  </div>

                  <div className="form-group checkbox-group">
                    <label>
                      <input type="checkbox" disabled checked={false} />
                      <span style={{color: '#999'}}>Email not sent for guest spots</span>
                    </label>
                  </div>
                </div>
              </div>

              {/* COMIC SHOW DETAILS */}
              <div className="booking-type-section">
                <h4>Show Details</h4>
                <div className="form-grid">
                  <div className="form-group">
                    <label htmlFor="show-date">Show Date *</label>
                    <input
                      type="date"
                      id="show-date"
                      value={newBooking.show_date}
                      onChange={(e) => setNewBooking({ ...newBooking, show_date: e.target.value })}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="show-time">Show Time *</label>
                    <input
                      type="time"
                      id="show-time"
                      value={newBooking.show_time}
                      onChange={(e) => setNewBooking({ ...newBooking, show_time: e.target.value })}
                    />
                  </div>

                  <div className="form-group checkbox-group">
                    <label>
                      <input
                        type="checkbox"
                        checked={newBooking.is_host}
                        onChange={(e) => setNewBooking({ ...newBooking, is_host: e.target.checked })}
                      />
                      Is Host
                    </label>
                  </div>
                </div>
              </div>
            </>
          ) : (
            /* STAFF BOOKING FORM */
            <>
              <div className="booking-type-section">
                <h4>Staff Member</h4>
                <div className="form-grid">
                  <div className="form-group autocomplete-container">
                    <label htmlFor="staff-search">Staff Name *</label>
                    <input
                      type="text"
                      id="staff-search"
                      value={staffSearchQuery}
                      onChange={(e) => handleStaffSearch(e.target.value)}
                      onFocus={() => staffSearchQuery && setShowStaffSuggestions(true)}
                      onBlur={() => setTimeout(() => setShowStaffSuggestions(false), 200)}
                      placeholder="Start typing staff name..."
                      autoComplete="off"
                    />
                    {showStaffSuggestions && filteredStaff.length > 0 && (
                      <ul className="autocomplete-suggestions">
                        {filteredStaff.map((staffMember) => (
                          <li
                            key={staffMember.email}
                            onClick={() => selectStaff(staffMember)}
                            className="suggestion-item"
                          >
                            <div className="suggestion-name">{staffMember.name}</div>
                            <div className="suggestion-email">{staffMember.email}</div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor="staff-email">Staff Email</label>
                    <input
                      type="email"
                      id="staff-email"
                      value={newBooking.staff_email}
                      onChange={(e) => setNewBooking({ ...newBooking, staff_email: e.target.value })}
                      placeholder="Auto-filled from selection"
                      readOnly
                    />
                  </div>

                  <div className="form-group checkbox-group">
                    <label>
                      <input
                        type="checkbox"
                        checked={newBooking.send_email}
                        onChange={(e) => setNewBooking({ ...newBooking, send_email: e.target.checked })}
                      />
                      Send confirmation email to staff
                    </label>
                  </div>
                </div>
              </div>

              {/* STAFF ROLES */}
              <div className="booking-type-section">
                <h4>Roles</h4>
                <div className="form-grid">
                  <div className="form-group roles-checkboxes">
                    <label className="roles-label">Select one or more roles:</label>
                    <div className="roles-options">
                      {['Setup', 'Seating', 'Check-in', 'Teardown', 'Camera'].map(role => (
                        <label key={role} className="role-checkbox">
                          <input
                            type="checkbox"
                            checked={newBooking.staff_roles.includes(role)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setNewBooking({
                                  ...newBooking,
                                  staff_roles: [...newBooking.staff_roles, role]
                                });
                              } else {
                                setNewBooking({
                                  ...newBooking,
                                  staff_roles: newBooking.staff_roles.filter(r => r !== role)
                                });
                              }
                            }}
                          />
                          <span>{role}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* STAFF SCHEDULE */}
              <div className="booking-type-section">
                <h4>Schedule (Optional)</h4>
                <div className="form-grid">
                  <div className="form-group">
                    <label htmlFor="staff-start-hour">Start Time</label>
                    <input
                      type="time"
                      id="staff-start-hour"
                      value={newBooking.staff_start_hour}
                      onChange={(e) => setNewBooking({ ...newBooking, staff_start_hour: e.target.value })}
                      placeholder="e.g., 17:00"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="staff-end-hour">End Time</label>
                    <input
                      type="time"
                      id="staff-end-hour"
                      value={newBooking.staff_end_hour}
                      onChange={(e) => setNewBooking({ ...newBooking, staff_end_hour: e.target.value })}
                      placeholder="e.g., 22:00"
                    />
                  </div>
                </div>
              </div>

              {/* STAFF DATE (no time needed) */}
              <div className="booking-type-section">
                <h4>Date</h4>
                <div className="form-grid">
                  <div className="form-group">
                    <label htmlFor="staff-date">Date *</label>
                    <input
                      type="date"
                      id="staff-date"
                      value={newBooking.show_date}
                      onChange={(e) => setNewBooking({ ...newBooking, show_date: e.target.value })}
                    />
                  </div>
                </div>
              </div>
            </>
          )}

          <div className="form-actions">
            <button onClick={handleAddBooking} className="submit-button">
              Create Booking
            </button>
            <button onClick={() => setShowAddForm(false)} className="cancel-button">
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="filters-panel">
        <h3>Filters</h3>
        <div className="filters-grid">
          <div className="filter-group">
            <label htmlFor="start-date">Start Date</label>
            <input
              type="date"
              id="start-date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>

          <div className="filter-group">
            <label htmlFor="end-date">End Date</label>
            <input
              type="date"
              id="end-date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
        </div>

        <div className="filter-actions">
          <button onClick={handleApplyFilters} className="apply-button">
            Apply Filters
          </button>
          <button onClick={handleClearFilters} className="clear-button">
            Clear Filters
          </button>
        </div>
      </div>

      {loading ? (
        <div className="loading">Loading bookings...</div>
      ) : bookingDays.length === 0 ? (
        <div className="no-data">No bookings found</div>
      ) : (
        <div className="booking-days-container">
          {bookingDays.map((day) => (
            <div key={day.date} className="booking-day-card">
              <h3 className="day-header">{day.date_display}</h3>

              {/* Staff Bookings Section */}
              {day.staff_bookings && day.staff_bookings.length > 0 && (
                <div className="staff-section">
                  <h4 className="section-title">📋 Staff</h4>
                  <table className="bookings-table staff-bookings-table">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Roles</th>
                        <th>Schedule</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {day.staff_bookings.map((staffBooking) => (
                        <tr
                          key={staffBooking.booking_id}
                          className={staffBooking.confirmed ? 'confirmed-row' : 'unconfirmed-row'}
                        >
                          <td className="staff-name">{staffBooking.staff_name}</td>
                          <td className="staff-email">{staffBooking.staff_email}</td>
                          <td>
                            {staffBooking.roles && staffBooking.roles.length > 0 ? (
                              <div className="staff-roles">
                                {staffBooking.roles.map((role, idx) => (
                                  <span key={idx} className="role-badge role-staff">
                                    {role}
                                  </span>
                                ))}
                              </div>
                            ) : (
                              '-'
                            )}
                          </td>
                          <td>
                            {staffBooking.start_hour && staffBooking.end_hour ? (
                              <span className="schedule-time">{staffBooking.start_hour} - {staffBooking.end_hour}</span>
                            ) : staffBooking.start_hour ? (
                              <span className="schedule-time">From {staffBooking.start_hour}</span>
                            ) : staffBooking.end_hour ? (
                              <span className="schedule-time">Until {staffBooking.end_hour}</span>
                            ) : (
                              '-'
                            )}
                          </td>
                          <td>
                            {staffBooking.confirmed ? (
                              <span className="status-badge status-confirmed">Confirmed</span>
                            ) : (
                              <span className="status-badge status-unconfirmed">Pending</span>
                            )}
                          </td>
                          <td className="actions-cell">
                            {!staffBooking.confirmed && (
                              <button
                                onClick={() => handleToggleStaffConfirmation(staffBooking.date, staffBooking.staff_email, staffBooking.confirmed, staffBooking.staff_name)}
                                className="toggle-button btn-confirm"
                              >
                                ✓ Confirm
                              </button>
                            )}
                            <button
                              onClick={() => handleDeleteStaffBooking(staffBooking.date, staffBooking.staff_email, staffBooking.staff_name)}
                              className="delete-button"
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Shows Section */}
              {day.shows && day.shows.length > 0 && (
                <div className="shows-section">
                  <h4 className="section-title">🎤 Shows</h4>
                  {day.shows.map((show) => (
                    <div key={show.show_date_time} className="show-card">
                      <div className="show-header">
                        <h5 className="show-time">{show.show_display}</h5>
                        <span className="show-count">{show.comic_bookings?.length || 0} comics</span>
                      </div>
                      {show.comic_bookings && show.comic_bookings.length > 0 ? (
                        <>
                          <div className="bookings-table">
                            <DndContext
                              sensors={sensors}
                              collisionDetection={closestCenter}
                              onDragEnd={(event) => handleDragEnd(event, show.show_date_time, show.comic_bookings || [])}
                            >
                              <table>
                                <thead>
                                  <tr>
                                    <th className="order-column">#</th>
                                    <th>Comic</th>
                                    <th>Role</th>
                                    <th>Set Length</th>
                                    <th>Amount Paid</th>
                                    <th>Status</th>
                                    <th>Email</th>
                                    <th>Actions</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  <SortableContext
                                    items={show.comic_bookings.map(b => `${b.show_date_time}-${b.comic_email}`)}
                                    strategy={verticalListSortingStrategy}
                                  >
                                    {show.comic_bookings.map((booking, index) => {
                                      const bookingKey = `${booking.show_date_time}-${booking.comic_email}`;
                                      const isUpdating = updatingBooking === bookingKey;

                                      return (
                                        <SortableBookingRow
                                          key={bookingKey}
                                          bookingKey={bookingKey}
                                          booking={booking}
                                          isUpdating={isUpdating}
                                          editingAmountPaid={editingAmountPaid}
                                          amountPaidValues={amountPaidValues}
                                          editingSetLength={editingSetLength}
                                          setLengthValues={setLengthValues}
                                          setEditingAmountPaid={setEditingAmountPaid}
                                          setAmountPaidValues={setAmountPaidValues}
                                          setEditingSetLength={setEditingSetLength}
                                          setSetLengthValues={setSetLengthValues}
                                          handleToggleConfirmation={handleToggleConfirmation}
                                          handleUpdateAmountPaid={handleUpdateAmountPaid}
                                          handleUpdateSetLength={handleUpdateSetLength}
                                          handleDelete={handleDeleteBooking}
                                          index={index}
                                        />
                                      );
                                    })}
                                  </SortableContext>
                                </tbody>
                              </table>
                            </DndContext>
                          </div>
                          {reorderingShow === show.show_date_time && (
                            <div className="lineup-save-indicator">
                              <span className="save-message">💾 Saving lineup order...</span>
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="no-comics">No comics booked yet</div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {(!day.staff_bookings || day.staff_bookings.length === 0) &&
                (!day.shows || day.shows.length === 0) && (
                  <div className="no-bookings-day">No bookings for this day</div>
                )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default BookingsList;
